import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Field, Input, Select, Spinner, Textarea } from '../ui.jsx';
import { RoofEstimator } from '../widgets/RoofEstimator.jsx';

// Calendly-style light palette. The rest of the app is dark-themed but a
// public booking page needs to look clean + trustworthy to end customers.
const LIGHT = {
  bg: '#f6f7fb', card: '#ffffff', border: '#e4e6ec', muted: '#5b6478',
  text: '#111827', accent: '#0f6dff', accentDim: '#e6efff', success: '#137e5f',
};

// Public booking page. URL: /book/:slug
//
// Two flows share this component:
//   1. Calendar-slug (Calendly-style) — /book/:slug maps to a `calendars` row
//      via public_slug. Renders: job-type picker → date grid → time slots that
//      respect connected Google Calendars (freeBusy union) → contact form.
//   2. Legacy account-slug — /book/:slug is an old account.slug booking form
//      with static date/time pickers. Preserved for any accounts still using
//      it. If both endpoints 404, we render an "invalid link" error.

export function BookingWidget() {
  const slug = window.location.pathname.split('/').filter(Boolean)[1];
  const [mode, setMode] = useState(null); // 'calendar' | 'legacy'
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!slug) { setErr('Invalid link'); return; }
    // Try calendar-first — Calendly-style flow is the future default.
    api(`/api/calendars/by-slug/${slug}`)
      .then(cal => { setData(cal); setMode('calendar'); })
      .catch(() => {
        api(`/api/booking-widget/${slug}`)
          .then(r => { setData(r); setMode('legacy'); })
          .catch(e => setErr(e.message));
      });
  }, [slug]);

  if (err) return <LightShell><div style={{ background:LIGHT.card, border:`1px solid ${LIGHT.border}`, borderRadius:14, padding:36, textAlign:'center', maxWidth:480, margin:'0 auto', boxShadow:'0 8px 40px rgba(15,23,42,.08)' }}>
    <div style={{ fontSize:44, marginBottom:10 }}>🔗</div>
    <h2 style={{ margin:0, color:LIGHT.text }}>Booking link not found</h2>
    <div style={{ color:LIGHT.muted, marginTop:10, fontSize:14 }}>This link isn't set up yet, or the URL was mistyped. Double-check the address or contact the business directly.</div>
  </div></LightShell>;
  if (!data) return <LightShell><div style={{ textAlign:'center', padding:40 }}><Spinner sz={36}/></div></LightShell>;

  return mode === 'calendar'
    ? <CalendlyView cal={data} slug={slug}/>
    : <LegacyView data={data} slug={slug}/>;
}

// ─── Calendly-style flow ──────────────────────────────────────────────────
// Two-column layout: left = business/service summary, right = date + time
// picker. Light theme forced regardless of the app's dark palette.
function CalendlyView({ cal, slug }) {
  const [step, setStep] = useState('type'); // type → picker → form → done
  const [selJobType, setSelJobType] = useState(() => cal.jobTypes?.[0] || null);
  const [selDate, setSelDate] = useState(null);
  const [selTime, setSelTime] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [form, setForm] = useState({ firstName:'', lastName:'', phone:'', email:'', address:'', notes:'' });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (!selDate) return;
    setLoadingAvail(true);
    const qs = new URLSearchParams({ date: selDate, ...(selJobType ? { jobTypeId: selJobType.id } : {}) });
    api(`/api/calendars/by-slug/${slug}/availability?${qs}`)
      .then(setAvailability)
      .catch(e => setErr(e.message))
      .finally(() => setLoadingAvail(false));
  }, [selDate, selJobType, slug]);

  useEffect(() => {
    if (step === 'type' && (!cal.jobTypes || cal.jobTypes.length <= 1)) setStep('picker');
  }, [step, cal.jobTypes]);

  const submit = async () => {
    if (!form.firstName || !form.phone) { setErr('First name and phone are required.'); return; }
    setSubmitting(true); setErr(null);
    try {
      const r = await api(`/api/calendars/by-slug/${slug}/book`, { method:'POST', body: {
        firstName: form.firstName, lastName: form.lastName, phone: form.phone,
        email: form.email, address: form.address, notes: form.notes,
        date: selDate, time: selTime, jobTypeId: selJobType?.id,
      }});
      setConfirmation(r);
      setStep('done');
    } catch (e) { setErr(e.message); }
    finally { setSubmitting(false); }
  };

  if (step === 'done') return <LightShell><ConfirmCard cal={cal} date={selDate} time={selTime} jobType={selJobType} form={form} confirmation={confirmation}/></LightShell>;

  return <LightShell>
    <div style={{
      background: LIGHT.card, border:`1px solid ${LIGHT.border}`, borderRadius:14,
      width:'100%', maxWidth: step === 'form' ? 640 : 920, margin:'0 auto',
      overflow:'hidden', boxShadow:'0 8px 40px rgba(15,23,42,.08)',
      display:'grid', gridTemplateColumns: step === 'form' ? '1fr' : 'minmax(240px, 320px) 1fr',
    }}>
      {step !== 'form' && <SummarySidebar cal={cal} selJobType={selJobType} selDate={selDate} selTime={selTime}/>}

      <div style={{ padding:'28px 30px', minHeight:520 }}>
        {step === 'type' && <div>
          <SectionH>Choose a service</SectionH>
          <div style={{ display:'grid', gap:10 }}>
            {(cal.jobTypes || []).map(jt => (
              <button key={jt.id} onClick={()=>{ setSelJobType(jt); setStep('picker'); }}
                style={{
                  textAlign:'left', padding:'16px 18px',
                  border:`1px solid ${LIGHT.border}`, borderLeft:`4px solid ${jt.color || LIGHT.accent}`,
                  background:'#fff', color:LIGHT.text, borderRadius:10, cursor:'pointer',
                  transition:'border-color .15s',
                }}
                onMouseEnter={e=>e.currentTarget.style.borderColor = LIGHT.accent}
                onMouseLeave={e=>e.currentTarget.style.borderColor = LIGHT.border}>
                <div style={{ fontWeight:700, fontSize:16 }}>{jt.name}</div>
                <div style={{ fontSize:13, color:LIGHT.muted, marginTop:3 }}>⏱ {jt.durationMinutes || 60} min{jt.description ? ` · ${jt.description}` : ''}</div>
              </button>
            ))}
          </div>
        </div>}

        {step === 'picker' && <div style={{ display:'grid', gridTemplateColumns: selDate ? '1fr 200px' : '1fr', gap:24 }}>
          <div>
            <SectionH>Select a Date</SectionH>
            <DateGrid onPick={d => { setSelDate(d); setSelTime(null); }} selected={selDate}
              maxAdvanceDays={cal.bookingRules?.maxAdvanceDays || 30}
              allowedDaysOfWeek={allowedDows(cal)}/>
          </div>
          {selDate && <div>
            <div style={{ fontWeight:700, fontSize:14, color:LIGHT.text, marginBottom:10 }}>{fmtDate(selDate).split(',')[0]}</div>
            <div style={{ display:'grid', gap:6, maxHeight:400, overflow:'auto', paddingRight:4 }}>
              {loadingAvail ? <div style={{ padding:14, textAlign:'center', color:LIGHT.muted }}>Loading…</div>
                : (availability?.slots?.length || 0) === 0
                  ? <div style={{ padding:12, color:LIGHT.muted, fontSize:13, textAlign:'center' }}>No open times.</div>
                  : availability.slots.map(t => (
                      <button key={t} onClick={()=>{ setSelTime(t); setStep('form'); }}
                        style={{
                          padding:'11px 12px', border:`1px solid ${LIGHT.accent}`,
                          background:'#fff', color:LIGHT.accent, borderRadius:8,
                          cursor:'pointer', fontWeight:700, fontSize:14, textAlign:'center',
                          transition:'background .12s',
                        }}
                        onMouseEnter={e=>{ e.currentTarget.style.background = LIGHT.accent; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = LIGHT.accent; }}>
                        {fmtTime(t)}
                      </button>
                    ))}
            </div>
          </div>}
          {cal.jobTypes?.length > 1 && <div style={{ gridColumn:'1/-1', marginTop:14 }}><BackLink onClick={()=>setStep('type')} label="← Change service"/></div>}
        </div>}

        {step === 'form' && <div style={{ maxWidth:520, margin:'0 auto' }}>
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:12, color:LIGHT.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em' }}>Enter details</div>
            <div style={{ fontSize:14, color:LIGHT.text, marginTop:6, display:'flex', gap:14, flexWrap:'wrap' }}>
              <span>📅 {fmtDate(selDate)}</span>
              <span>🕒 {fmtTime(selTime)}</span>
              {selJobType && <span>🔧 {selJobType.name}</span>}
            </div>
          </div>
          <LightForm form={form} setForm={setForm}/>
          {String(cal.industry || '').toLowerCase() === 'roofing' && form.address && (
            <div style={{ marginTop:14 }}>
              <RoofEstimator address={form.address} mode="public" theme="light"/>
            </div>
          )}
          {err && <div style={{ color:'#c92026', fontSize:13, marginBottom:10 }}>{err}</div>}
          <button onClick={submit} disabled={submitting}
            style={{ width:'100%', padding:'14px', background: LIGHT.accent, color:'#fff', border:'none', borderRadius:10, fontWeight:800, fontSize:15, cursor: submitting?'wait':'pointer' }}>
            {submitting ? 'Booking…' : 'Confirm Booking'}
          </button>
          <BackLink onClick={()=>setStep('picker')} label="← Change time"/>
        </div>}
      </div>
    </div>
    <div style={{ textAlign:'center', marginTop:14, fontSize:11, color:LIGHT.muted }}>Powered by TradeOps</div>
  </LightShell>;
}

function SummarySidebar({ cal, selJobType, selDate, selTime }) {
  return <div style={{ background:'#fafbfd', borderRight:`1px solid ${LIGHT.border}`, padding:'28px 24px' }}>
    <div style={{ marginBottom:22 }}>
      {cal.business?.logoUrl
        ? <img src={cal.business.logoUrl} alt="" style={{ maxHeight:48, marginBottom:10 }}/>
        : <div style={{ width:48, height:48, borderRadius:24, background:LIGHT.accentDim, color:LIGHT.accent, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:20, marginBottom:10 }}>{(cal.business?.name || 'B')[0]}</div>}
      <div style={{ fontSize:14, color:LIGHT.muted, fontWeight:600 }}>{cal.business?.name || 'Book with us'}</div>
      <div style={{ fontSize:20, color:LIGHT.text, fontWeight:800, marginTop:4, lineHeight:1.3 }}>{selJobType?.name || cal.name}</div>
    </div>
    <div style={{ fontSize:13, color:LIGHT.muted, lineHeight:1.7 }}>
      <div>⏱ {selJobType?.durationMinutes || cal.bookingRules?.slotDurationMinutes || 60} minutes</div>
      {selDate && <div>📅 {fmtDate(selDate)}</div>}
      {selTime && <div>🕒 {fmtTime(selTime)}</div>}
    </div>
    {(selJobType?.description || cal.description) && (
      <div style={{ marginTop:20, paddingTop:20, borderTop:`1px solid ${LIGHT.border}`, fontSize:13, color:LIGHT.muted, lineHeight:1.55 }}>
        {selJobType?.description || cal.description}
      </div>
    )}
    {cal.business?.phone && <div style={{ marginTop:20, paddingTop:20, borderTop:`1px solid ${LIGHT.border}`, fontSize:12, color:LIGHT.muted }}>
      Need help? <a href={`tel:${cal.business.phone}`} style={{ color:LIGHT.accent }}>{cal.business.phone}</a>
    </div>}
  </div>;
}

function LightForm({ form, setForm }) {
  const inp = { width:'100%', padding:'11px 12px', border:`1px solid ${LIGHT.border}`, borderRadius:8, fontSize:14, background:'#fff', color:LIGHT.text, boxSizing:'border-box', fontFamily:'inherit' };
  const lbl = { display:'block', fontSize:12, fontWeight:700, color:LIGHT.text, marginBottom:6 };
  const row = { marginBottom:12 };
  return <div>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
      <div style={row}><label style={lbl}>First name *</label><input style={inp} value={form.firstName} onChange={e=>setForm({...form, firstName:e.target.value})}/></div>
      <div style={row}><label style={lbl}>Last name</label><input style={inp} value={form.lastName} onChange={e=>setForm({...form, lastName:e.target.value})}/></div>
    </div>
    <div style={row}><label style={lbl}>Phone *</label><input style={inp} value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} placeholder="(555) 555-0100"/></div>
    <div style={row}><label style={lbl}>Email</label><input style={inp} type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/></div>
    <div style={row}><label style={lbl}>Service address</label><input style={inp} value={form.address} onChange={e=>setForm({...form, address:e.target.value})}/></div>
    <div style={row}><label style={lbl}>Anything we should know?</label><textarea style={{ ...inp, minHeight:80, resize:'vertical' }} value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} placeholder="Optional notes for the tech"/></div>
  </div>;
}

function ConfirmCard({ cal, date, time, jobType, form, confirmation }) {
  return <div style={{ background:LIGHT.card, border:`1px solid ${LIGHT.border}`, borderRadius:14, padding:36, textAlign:'center', maxWidth:520, margin:'0 auto', boxShadow:'0 8px 40px rgba(15,23,42,.08)' }}>
    <div style={{ width:64, height:64, borderRadius:32, background:'#dcfce7', color:LIGHT.success, display:'flex', alignItems:'center', justifyContent:'center', fontSize:32, margin:'0 auto 14px', fontWeight:800 }}>✓</div>
    <h2 style={{ margin:0, color:LIGHT.text, fontSize:22 }}>You're all booked</h2>
    <div style={{ marginTop:18, padding:16, background:'#fafbfd', borderRadius:10, textAlign:'left', fontSize:14, color:LIGHT.text, lineHeight:1.7 }}>
      <div>📅 {fmtDate(date)} at {fmtTime(time)}</div>
      {jobType && <div>🔧 {jobType.name} ({jobType.durationMinutes || 60} min)</div>}
      <div>👤 {form.firstName} {form.lastName}</div>
      {form.address && <div>📍 {form.address}</div>}
      {confirmation?.confirmationCode && <div style={{ marginTop:8, fontSize:12, color:LIGHT.muted }}>Confirmation: <strong>{confirmation.confirmationCode}</strong></div>}
    </div>
    <div style={{ color:LIGHT.muted, marginTop:16, fontSize:13 }}>
      We'll send a confirmation shortly. Questions? {cal.business?.phone ? <a href={`tel:${cal.business.phone}`} style={{ color:LIGHT.accent }}>Call {cal.business.phone}</a> : 'Call the office.'}
    </div>
  </div>;
}

function DateGrid({ onPick, selected, maxAdvanceDays, allowedDaysOfWeek }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const now = new Date();
  const view = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const firstDow = view.getDay();
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const cells = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(view.getFullYear(), view.getMonth(), i+1)),
  ];
  const iso = (d) => d.toISOString().slice(0,10);
  const today0 = new Date(); today0.setHours(0,0,0,0);
  const maxDate = new Date(today0); maxDate.setDate(today0.getDate() + maxAdvanceDays);
  return <div>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
      <button onClick={()=>setMonthOffset(o=>o-1)} disabled={monthOffset<=0} style={navBtn(monthOffset<=0)}>‹</button>
      <div style={{ fontWeight:700, color:LIGHT.text }}>{view.toLocaleDateString([], { month:'long', year:'numeric' })}</div>
      <button onClick={()=>setMonthOffset(o=>o+1)} style={navBtn(false)}>›</button>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:4, marginBottom:6 }}>
      {['S','M','T','W','T','F','S'].map((l, i) => <div key={i} style={{ textAlign:'center', color:LIGHT.muted, fontSize:11, fontWeight:700, padding:'4px 0' }}>{l}</div>)}
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:4 }}>
      {cells.map((d, i) => {
        if (!d) return <div key={i}/>;
        const past = d < today0;
        const beyond = d > maxDate;
        const disallowed = allowedDaysOfWeek && !allowedDaysOfWeek.includes(d.getDay());
        const disabled = past || beyond || disallowed;
        const isSel = selected === iso(d);
        return <button key={i} onClick={()=>!disabled && onPick(iso(d))} disabled={disabled}
          style={{
            aspectRatio:'1', borderRadius:'50%', border:'none', fontSize:13, fontWeight:600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            background: isSel ? LIGHT.accent : (disabled ? 'transparent' : LIGHT.accentDim),
            color: isSel ? '#fff' : (disabled ? '#c9cdd6' : LIGHT.accent),
            transition: 'transform .1s',
          }}
          onMouseEnter={e=>!disabled && !isSel && (e.currentTarget.style.background = LIGHT.accent, e.currentTarget.style.color = '#fff')}
          onMouseLeave={e=>!disabled && !isSel && (e.currentTarget.style.background = LIGHT.accentDim, e.currentTarget.style.color = LIGHT.accent)}>
          {d.getDate()}
        </button>;
      })}
    </div>
  </div>;
}
function navBtn(disabled) {
  return { width:32, height:32, borderRadius:16, border:'none', background:LIGHT.card, color: disabled?'#c9cdd6':LIGHT.text, fontSize:20, cursor: disabled?'not-allowed':'pointer' };
}

function allowedDows(cal) {
  const wh = cal.bookingRules?.workingHours || {};
  const map = { sun:0, mon:1, tue:2, wed:3, thu:4, fri:5, sat:6 };
  return Object.keys(wh).filter(k => (wh[k] || []).length > 0).map(k => map[k]);
}
function LightShell({ children }) {
  return <div style={{ minHeight:'100vh', background:LIGHT.bg, padding:'40px 20px', fontFamily:'system-ui,-apple-system,Segoe UI,Roboto,sans-serif' }}>
    {children}
  </div>;
}
function SectionH({ children }) { return <div style={{ fontSize:18, fontWeight:800, color:LIGHT.text, marginBottom:14 }}>{children}</div>; }
function BackLink({ onClick, label = '← Back' }) { return <div style={{ textAlign:'center', marginTop:14 }}><button onClick={onClick} style={{ background:'none', border:'none', color:LIGHT.muted, fontSize:13, cursor:'pointer' }}>{label}</button></div>; }
function fmtDate(iso) { const d = new Date(iso + 'T12:00:00'); return d.toLocaleDateString([], { weekday:'long', month:'long', day:'numeric' }); }
function fmtTime(hhmm) { const [h,m] = hhmm.split(':').map(Number); const d = new Date(); d.setHours(h,m,0,0); return d.toLocaleTimeString([], { hour:'numeric', minute:'2-digit' }); }

// ─── Legacy account-slug flow (unchanged) ─────────────────────────────────
function LegacyView({ data, slug }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '',
    address: '', city: '', state: '', zip: '',
    service: '', preferredDate: '', preferredTime: '09:00', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);

  if (done) return <Shell>
    <div style={{ ...styles.card, padding:30, textAlign:'center', maxWidth:480, margin:'0 auto' }}>
      <div style={{ fontSize:60, marginBottom:10 }}>✓</div>
      <h2 style={{ margin:0, color:G.green }}>You're booked!</h2>
      <p style={{ color:G.textMuted, marginTop:8 }}>{done.message}</p>
    </div>
  </Shell>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.phone) { setErr('Name and phone required'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await api(`/api/booking-widget/${slug}/submit`, { method:'POST', body: form });
      setDone(r);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const accent = data.branding?.primary || G.purple;
  return <Shell>
    <div style={{ ...styles.card, padding:30, maxWidth:520, width:'100%', margin:'0 auto' }}>
      <div style={{ textAlign:'center', marginBottom:20 }}>
        {data.branding?.logoUrl
          ? <img src={data.branding.logoUrl} alt={data.business.name} style={{ maxHeight:64 }}/>
          : <div style={{ fontSize:22, fontWeight:800, color:G.text }}>{data.business.name}</div>}
        <div style={{ color:G.textMuted, fontSize:13, marginTop:6 }}>{data.intro}</div>
      </div>

      <form onSubmit={submit}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <Field label="First name *"><Input value={form.firstName} onChange={set('firstName')} required/></Field>
          <Field label="Last name"><Input value={form.lastName} onChange={set('lastName')}/></Field>
        </div>
        <Field label="Phone *"><Input value={form.phone} onChange={set('phone')} placeholder="(813) 555-0100" required/></Field>
        <Field label="Email"><Input type="email" value={form.email} onChange={set('email')}/></Field>
        <Field label="Service address"><Input value={form.address} onChange={set('address')}/></Field>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:10 }}>
          <Field label="City"><Input value={form.city} onChange={set('city')}/></Field>
          <Field label="State"><Input value={form.state} onChange={set('state')} maxLength={2}/></Field>
          <Field label="ZIP"><Input value={form.zip} onChange={set('zip')} maxLength={10}/></Field>
        </div>
        <Field label="What do you need? *">
          <Select value={form.service} onChange={set('service')} required>
            <option value="">— Select —</option>
            {data.services.map(s => <option key={s}>{s}</option>)}
          </Select>
        </Field>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <Field label="Preferred date"><Input type="date" value={form.preferredDate} onChange={set('preferredDate')}/></Field>
          <Field label="Preferred time"><Input type="time" value={form.preferredTime} onChange={set('preferredTime')}/></Field>
        </div>
        <Field label="Anything we should know?"><Textarea value={form.notes} onChange={set('notes')} placeholder="Optional notes for the technician"/></Field>

        {err && <div style={{ color:G.red, fontSize:13, marginBottom:8 }}>{err}</div>}

        <Btn v="primary" sz="lg" type="submit" full disabled={busy} style={{ background: accent, borderColor: accent }}>
          {busy ? <Spinner/> : 'Book My Appointment'}
        </Btn>
        <div style={{ fontSize:11, color:G.textMuted, textAlign:'center', marginTop:10 }}>
          By submitting you agree to receive SMS + email confirmations from {data.business.name}. Reply STOP to opt out.
        </div>
      </form>
    </div>
  </Shell>;
}

function Shell({ children }) {
  return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:24, background:G.bg }}>
    <div style={{ width:'100%', maxWidth:560 }}>{children}</div>
  </div>;
}
